import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {AgentType, ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import {AISettingsTab} from 'llm/chat-library/sidepanel/AISettings';
import {openSettings} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
  doesAssistantDeprecateAgent,
  getAgentDescriptionForChatInput,
  getAgentTypeText,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import UploadSkillModal from 'llm/chat-library/skills/UploadSkillModal';
import useSkills from 'llm/chat-library/skills/useSkills';
import AIButton from 'llm/chat-library/ui/AIButton';
import AIMenu from 'llm/chat-library/ui/AIMenu';
import {
  type DisabledServer,
  type EnabledServer,
  useMCPEnabledServersOptional,
} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import ConnectMCPModal from 'nav/sidepanel/mcps/ConnectMCPModal';
import {useMcpDirectoryInstallHandler} from 'tenants/admin/extensions/mcp/useMcpDirectoryInstallHandler';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuSelectedIconPlacement, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

const PLUS_MENU_NO_SELECTION = '__plus_menu_no_selection__';

function McpServerIcon({server}: {server: EnabledServer | DisabledServer}): JSX.Element {
  if (server.iconUrl != null) {
    return (
      <img
        src={staticURL(server.iconUrl)}
        alt=""
        className={css(styles.serverIconImg)}
        {...getTestProps({component: 'PlusMenu', element: 'mcp-server-icon', key: server.id})}
      />
    );
  }
  // TODO(BNCH-151518): fall back to the icon from the MCP `initialize` response.
  return <BenchlingIcon icon="sources" />;
}

export default function PlusMenu({
  fileInputRef,
  agentType,
  availableAgentTypes,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  onSelectSkill,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  agentType: AgentType | null;
  availableAgentTypes?: Array<AgentType>;
  setAgentType?: (agentType: AgentType | null) => void;
  inputDisabled: boolean;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  onSelectSkill?: (skillName: string) => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredDisabledServerId, setHoveredDisabledServerId] = useState<string | null>(null);
  const onMcpDirectoryInstall = useMcpDirectoryInstallHandler();
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const isMcpServersLoading = mcpEnabledServers?.isLoading ?? false;
  const hasTenantMcpConfigs = mcpEnabledServers?.hasTenantMcpConfigs ?? false;
  const enabledServersList = mcpEnabledServers?.enabledServers;
  const disabledServersList = mcpEnabledServers?.disabledServers;
  const enabledServers = useMemo(() => enabledServersList ?? [], [enabledServersList]);
  const disabledServers = useMemo(() => disabledServersList ?? [], [disabledServersList]);

  const showPublicData = agentType != null && doesAgentTypeSupportPublicData(agentType);
  const showMcpSection =
    agentType != null && doesAgentTypeSupportSources(agentType) && setSelectedMcpServerIds != null;
  const hasCoScientist =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
    availableAgentTypes?.includes(AgentType.CO_SCIENTIST) === true;
  const legacyAgents = useMemo(
    () =>
      BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT && availableAgentTypes != null
        ? availableAgentTypes.filter((agentOption) => doesAssistantDeprecateAgent(agentOption))
        : [],
    [availableAgentTypes]
  );
  const showAssistantAgentSection =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
    setAgentType != null &&
    (hasCoScientist || legacyAgents.length > 0);
  const selectedAssistantMenuAgentValue =
    showAssistantAgentSection &&
    agentType != null &&
    (agentType === AgentType.CO_SCIENTIST || legacyAgents.includes(agentType))
      ? agentType
      : PLUS_MENU_NO_SELECTION;

  const effectiveSelectedIds = selectedMcpServerIds ?? enabledServers.map((s) => s.id);

  const handleToggleServer = useCallback(
    (serverId: string): void => {
      if (!setSelectedMcpServerIds) {
        return;
      }
      const isCurrentlySelected = effectiveSelectedIds.includes(serverId);
      if (isCurrentlySelected) {
        setSelectedMcpServerIds(effectiveSelectedIds.filter((id) => id !== serverId));
      } else {
        const newIds = [...effectiveSelectedIds, serverId];
        if (newIds.length === enabledServers.length) {
          setSelectedMcpServerIds(undefined);
        } else {
          setSelectedMcpServerIds(newIds);
        }
      }
    },
    [effectiveSelectedIds, enabledServers, setSelectedMcpServerIds]
  );

  const handleAttachClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleManageMCPs = useCallback((): void => {
    openSettings(AISettingsTab.AI_CONNECTORS);
  }, []);

  const showSkillsSection = agentType != null && onSelectSkill != null;

  const {skills, createNewSkills} = useSkills({enabled: showSkillsSection});

  const handleManageSkills = useCallback(() => {
    openSettings(AISettingsTab.SKILLS);
  }, []);

  const handleAddSkill = useCallback((): void => {
    legacyRunModal(UploadSkillModal, {
      onRequestSubmit: createNewSkills,
    });
  }, [createNewSkills]);

  const handleBrowseMcpDirectory = useCallback((): void => {
    legacyRunModal(ContentStoreModal, {
      extensionType: ExtensionType.MCP_SERVER,
      onInstall: onMcpDirectoryInstall,
    });
  }, [onMcpDirectoryInstall]);

  const closeMenu = useCallback((): void => {
    setIsOpen(false);
    setHoveredDisabledServerId(null);
  }, []);

  const handleConnectMcpServer = useCallback(
    (server: {id: string; name: string; authType: string}): void => {
      legacyRunModal(ConnectMCPModal, {
        config: {
          id: server.id,
          name: server.name,
          authType: server.authType,
        },
        onRequestSubmit: () => {
          mcpEnabledServers?.refetch();
        },
      });
    },
    [mcpEnabledServers]
  );

  const menu = useMemo(
    () => (
      <AIMenu
        scrollable={false}
        height={MenuHeight.SMALL}
        width={MenuWidth.XLARGE}
        value={selectedAssistantMenuAgentValue}
        useSelectedIconIfSingleSelect={true}
        selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
      >
        <Section>
          <Option
            contentClassName={css(styles.menuOptionContent)}
            childrenClassName={css(styles.menuOptionChildren)}
            value="attach-files"
            icon="paperclip"
            isDisabled={inputDisabled}
            onSelect={() => {
              handleAttachClick();
              closeMenu();
            }}
          >
            Add photos and files
          </Option>
          {showPublicData && (
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="public-data"
              icon="globe"
              isDisabled={!isOpenAIEnabled}
              onSelect={() => {
                if (isOpenAIEnabled) {
                  setPublicDataEnabled(!publicDataEnabled);
                }
              }}
            >
              <span className={css(styles.toggleRow)}>
                <span>Public data</span>
                <Switch
                  checked={publicDataEnabled}
                  disabled={!isOpenAIEnabled}
                  onChange={() => {}}
                  size="xs"
                  styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                />
              </span>
            </Option>
          )}
        </Section>
        {showAssistantAgentSection && (
          <Section>
            {hasCoScientist && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value={AgentType.CO_SCIENTIST}
                icon="flask"
                onSelect={() => {
                  setAgentType?.(
                    agentType === AgentType.CO_SCIENTIST ? AgentType.ASSISTANT : AgentType.CO_SCIENTIST
                  );
                  closeMenu();
                }}
                metadata={
                  getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST) != null ? (
                    <span className={css(styles.agentMenuOptionSubtitle)}>
                      {getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST)}
                    </span>
                  ) : null
                }
              >
                {getAgentTypeText(AgentType.CO_SCIENTIST)}
              </Option>
            )}
            {legacyAgents.length > 0 && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value="agent-legacy-submenu"
                icon="sparkles"
              >
                Legacy agents
                <Menu
                  width={MenuWidth.LARGE}
                  value={selectedAssistantMenuAgentValue}
                  useSelectedIconIfSingleSelect={true}
                  selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
                >
                  {legacyAgents.map((agentOption) => (
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      key={agentOption}
                      value={agentOption}
                      onSelect={() => {
                        setAgentType?.(agentType === agentOption ? AgentType.ASSISTANT : agentOption);
                        closeMenu();
                      }}
                      metadata={
                        getAgentDescriptionForChatInput(agentOption) != null ? (
                          <span className={css(styles.agentMenuOptionSubtitle)}>
                            {getAgentDescriptionForChatInput(agentOption)}
                          </span>
                        ) : null
                      }
                    >
                      {getAgentTypeText(agentOption)}
                    </Option>
                  ))}
                </Menu>
              </Option>
            )}
          </Section>
        )}
        {showMcpSection && (
          <Section>
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="mcp-servers-submenu"
              icon="sources"
            >
              {MCP_PRODUCT_NAME_PLURAL}
              <Menu width={MenuWidth.MEDIUM}>
                {isMcpServersLoading ? (
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="mcp-loading"
                    isDisabled={true}
                  >
                    Loading...
                  </Option>
                ) : enabledServers.length === 0 && disabledServers.length === 0 ? (
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="mcp-none"
                    isDisabled={true}
                  >
                    {hasTenantMcpConfigs
                      ? `${MCP_PRODUCT_NAME_PLURAL} are available, but must be connected first.`
                      : `Ask your admin to make ${MCP_PRODUCT_NAME_PLURAL} available for your workspace.`}
                  </Option>
                ) : (
                  enabledServers.map((server) => (
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      avatarClassName="menu-option-iconWrapper"
                      key={server.id}
                      value={`mcp-${server.id}`}
                      avatar={<McpServerIcon server={server} />}
                      onSelect={() => {
                        handleToggleServer(server.id);
                      }}
                    >
                      <span className={css(styles.toggleRow)}>
                        <span>{server.name}</span>
                        <Switch
                          checked={effectiveSelectedIds.includes(server.id)}
                          onChange={() => {}}
                          size="xs"
                          styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                        />
                      </span>
                    </Option>
                  ))
                )}
                {disabledServers.length > 0 && (
                  <Section>
                    {disabledServers.map((server) => {
                      const isHovered = hoveredDisabledServerId === server.id;
                      return (
                        <Option
                          key={`mcp-disabled-${server.id}`}
                          contentClassName={css(styles.menuOptionContent)}
                          childrenClassName={css(styles.menuOptionChildren)}
                          value={`mcp-disabled-${server.id}`}
                          avatar={<McpServerIcon server={server} />}
                          avatarClassName="menu-option-iconWrapper"
                          onHover={() => {
                            setHoveredDisabledServerId(server.id);
                          }}
                          onMouseOut={() => {
                            setHoveredDisabledServerId((prevId) => (prevId === server.id ? null : prevId));
                          }}
                          onSelect={() => {
                            closeMenu();
                            handleConnectMcpServer(server);
                          }}
                        >
                          <span className={css(styles.connectOptionRow)}>
                            <span className={css(styles.connectOptionName)}>{server.name}</span>
                            <span
                              className={css(
                                styles.connectOptionAction,
                                isHovered && styles.connectOptionActionVisible
                              )}
                            >
                              Connect
                            </span>
                          </span>
                        </Option>
                      );
                    })}
                  </Section>
                )}
                {hasTenantMcpConfigs && (
                  <Section>
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="manage-mcps"
                      icon="gear"
                      onSelect={() => {
                        closeMenu();
                        handleManageMCPs();
                      }}
                    >
                      Manage connectors
                    </Option>
                  </Section>
                )}
                <Section>
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="browse-mcp-directory"
                    icon="store"
                    onSelect={() => {
                      closeMenu();
                      handleBrowseMcpDirectory();
                    }}
                  >
                    Browse directory
                  </Option>
                </Section>
              </Menu>
            </Option>
          </Section>
        )}
        {showSkillsSection && (
          <Section>
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="skills-submenu"
              icon="file-lines"
              dataTestAttrs={{component: 'PlusMenu', element: 'skillsSubmenuTrigger'}}
            >
              Skills
              <Menu width={MenuWidth.MEDIUM} height={MenuHeight.SMALL}>
                {skills.map((skill) => (
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    key={skill.id}
                    value={`skill-${skill.id}`}
                    onSelect={() => {
                      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_SKILL_FROM_PLUS_MENU, {
                        skillId: skill.id,
                        agentType,
                      });
                      onSelectSkill(skill.name);
                      closeMenu();
                    }}
                    dataTestAttrs={{component: 'PlusMenu', element: 'skillItem', key: skill.id}}
                  >
                    {skill.name}
                  </Option>
                ))}
                {skills.length === 0 && (
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="no-skills"
                    isDisabled={true}
                  >
                    No skills available
                  </Option>
                )}
                <Section>
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="manage-skills"
                    icon="settings"
                    onSelect={() => {
                      closeMenu();
                      handleManageSkills();
                    }}
                  >
                    Manage skills...
                  </Option>
                  <Option
                    contentClassName={css(styles.menuOptionContent)}
                    childrenClassName={css(styles.menuOptionChildren)}
                    value="add-skill"
                    icon="plus"
                    onSelect={() => {
                      closeMenu();
                      handleAddSkill();
                    }}
                  >
                    Add skill...
                  </Option>
                </Section>
              </Menu>
            </Option>
          </Section>
        )}
      </AIMenu>
    ),
    [
      closeMenu,
      disabledServers,
      effectiveSelectedIds,
      enabledServers,
      handleAddSkill,
      handleConnectMcpServer,
      handleManageSkills,
      hasTenantMcpConfigs,
      handleAttachClick,
      handleBrowseMcpDirectory,
      handleManageMCPs,
      handleToggleServer,
      isMcpServersLoading,
      hoveredDisabledServerId,
      inputDisabled,
      isOpenAIEnabled,
      onSelectSkill,
      publicDataEnabled,
      setPublicDataEnabled,
      showMcpSection,
      showAssistantAgentSection,
      showPublicData,
      hasCoScientist,
      legacyAgents,
      selectedAssistantMenuAgentValue,
      agentType,
      setAgentType,
      showSkillsSection,
      skills,
    ]
  );

  return (
    <Dropdown
      menu={menu}
      isOpen={isOpen}
      onToggle={setIsOpen}
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      allowMenuToCloseDropdown={false}
    >
      <AIButton
        ariaLabel="More options"
        className="assistant-ai-chat-plus-button"
        disabled={inputDisabled}
        icon="plus"
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  connectOptionAction: {
    color: Color.BLUE_500,
    fontSize: '12px',
    fontWeight: 600,
    opacity: 0,
    transition: 'opacity 100ms ease-in-out',
    flex: 0,
    display: 'none',
  },
  connectOptionActionVisible: {
    opacity: 1,
    display: 'block',
  },
  connectOptionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    width: '100%',
    // connectOptionName will ellipsize, so we don't need to wrap the text.
    wordBreak: 'normal',
  },
  connectOptionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuOptionContent: {
    gap: 6,
    padding: '0 14px 0 20px',
  },
  menuOptionChildren: {
    flex: 1,
    minWidth: 0,
  },
  serverIconImg: {
    width: 16,
    height: 16,
    objectFit: 'contain',
  },
  agentMenuOptionSubtitle: {
    fontSize: '12px',
    color: Color.GRAY_700,
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    minWidth: 0,
  },
});
